/**
 * The editors' shared icons, as SVG path data in one 16 by 16 view box, so
 * every platform draws the same marks with its own SVG: each icon is a list
 * of paths, every one stroked in the icon's color with round caps and joins,
 * never filled, at `iconStrokeWidth` for the size it is drawn at, so every
 * icon's lines read at one light weight on screen, whatever its size.
 */

export const ICON_VIEW_BOX = '0 0 16 16';

/** The size the editors draw their menu icons at (px on the web, pt on Kai Pro): the gear, the eye, the export arrow. */
export const ICON_SIZE = 14;

/** The stroke, in view box units, of an icon drawn at `ICON_SIZE`. */
export const ICON_STROKE_WIDTH = 1.5;

/** The stroke, in view box units, of an icon drawn `drawnSize` across: as heavy on screen as the menu icons' at `ICON_SIZE`. */
export const iconStrokeWidth = (drawnSize: number): number => (ICON_STROKE_WIDTH * ICON_SIZE) / drawnSize;

/** An icon: its paths, each drawn as `<path d={path} fill='none' stroke={color} />`. */
export type IconPaths = readonly string[];

const round = (value: number): string => String(Math.round(value * 100) / 100);

/** A circle about (centerX, centerY) as two half arcs. */
const circlePath = (centerX: number, centerY: number, radius: number): string =>
  `M${round(centerX + radius)} ${round(centerY)}A${round(radius)} ${round(radius)} 0 1 1 ${round(centerX - radius)} ${round(centerY)}A${round(radius)} ${
    round(radius)
  } 0 1 1 ${round(centerX + radius)} ${round(centerY)}Z`;

/** A gear's toothed rim about the view box's center: eight teeth, each tapering from its root to its flat tip. */
function gearRimPath(): string {
  const teeth = 8, tipRadius = 7.1, rootRadius = 5.2, tipHalfTurn = (7 * Math.PI) / 180, rootHalfTurn = (11 * Math.PI) / 180;
  const at = (radius: number, turn: number): string => `${round(8 + radius * Math.sin(turn))} ${round(8 - radius * Math.cos(turn))}`;
  const toothPaths = Array.from({ length: teeth }, (_, tooth) => {
    const middle = (2 * Math.PI * tooth) / teeth;
    return `L${at(tipRadius, middle - tipHalfTurn)}A${tipRadius} ${tipRadius} 0 0 1 ${at(tipRadius, middle + tipHalfTurn)}`
      + `L${at(rootRadius, middle + rootHalfTurn)}A${rootRadius} ${rootRadius} 0 0 1 ${at(rootRadius, middle + (2 * Math.PI) / teeth - rootHalfTurn)}`;
  });
  return `M${at(rootRadius, -rootHalfTurn)}${toothPaths.join('')}Z`;
}

/** The Settings menu's gear: a toothed rim around an axle hole. */
export const GEAR_ICON: IconPaths = [gearRimPath(), circlePath(8, 8, 2.2)];

/** The View menu's eye: an almond and its pupil. */
export const EYE_ICON: IconPaths = ['M1.2 8C2.8 5 5.2 3.4 8 3.4S13.2 5 14.8 8C13.2 11 10.8 12.6 8 12.6S2.8 11 1.2 8Z', circlePath(8, 8, 2.2)];

/** The view cube's Home: a house, drawn in its button's disc. */
export const HOME_ICON: IconPaths = ['M4.4 8.3 8 5l3.6 3.3', 'M5.6 7.3V11h4.8V7.3'];

/**
 * A spin button's arrow, drawn in its button's disc: three quarters of a ring around a
 * vertical axis, seen from a little above, running across the back and down
 * the left to end at the front right with its head pointing right, the way
 * the front of the house turns. `mirrored` flips it to turn left.
 */
function turnIconPaths(mirrored: boolean): IconPaths {
  const centerX = 8, centerY = 8.4, radiusX = 4.8, radiusY = 2.4, startTurn = (-40 * Math.PI) / 180, endTurn = (50 * Math.PI) / 180;
  const headLength = 2.1, headSpread = (40 * Math.PI) / 180;
  const ring = (turn: number): [number, number] => [centerX + radiusX * Math.cos(turn), centerY + radiusY * Math.sin(turn)];
  const point = ([x, y]: [number, number]): string => `${round(mirrored ? 16 - Number(round(x)) : x)} ${round(y)}`;
  const [endX, endY] = ring(endTurn);
  const backward = Math.atan2(radiusY * Math.cos(endTurn), -radiusX * Math.sin(endTurn));
  const barb = (spread: number): [number, number] => [endX + headLength * Math.cos(backward + spread), endY + headLength * Math.sin(backward + spread)];
  return [
    `M${point(ring(startTurn))}A${radiusX} ${radiusY} 0 1 ${mirrored ? 1 : 0} ${point([endX, endY])}`,
    `M${point(barb(headSpread))}L${point([endX, endY])}L${point(barb(-headSpread))}`,
  ];
}

/** The view cube's spin right: the house turns right a quarter turn, as if circling it. */
export const TURN_RIGHT_ICON: IconPaths = turnIconPaths(false);

/** The view cube's spin left: the mirror image of spin right. */
export const TURN_LEFT_ICON: IconPaths = turnIconPaths(true);
